import { useEffect, useState } from "react";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { api, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { Kpi, PageHead } from "@/components/Bits";
import { CustomAnalysis } from "@/components/CustomAnalysis";
import { HqB2BProfit } from "@/components/HqB2BProfit";
import { useConfig } from "@/lib/useConfig";
import { ExportButtons } from "@/components/ExportButtons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Profit() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [leads, setLeads] = useState(null);
  const [brand, setBrand] = useState(null);
  const [sort, setSort] = useState("profit");
  const [studios, setStudios] = useState([]);
  const [scopeStudio, setScopeStudio] = useState("");
  const [rollup, setRollup] = useState(null);
  const [rollupSort, setRollupSort] = useState("profit");
  const [month, setMonth] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const services = useConfig("services");
  const isAdmin = can(user, "studios:write");
  const isProductManager = user.role === "sales_manager_products";

  useEffect(() => {
    if (isProductManager) return;
    api.get("/reports/summary").then(({ data }) => setSummary(data));
    api.get("/jobs", { params: { status: "Delivered" } }).then(({ data }) => setJobs(data));
    if (can(user, "studios:write")) {
      api.get("/reports/lead-sources").then(({ data }) => setLeads(data));
      api.get("/studios").then(({ data }) => setStudios(data));
    }
  }, [user.role]);

  useEffect(() => {
    if (isProductManager) return;
    const params = scopeStudio ? { studio_id: scopeStudio } : {};
    api.get("/reports/brand-model", { params }).then(({ data }) => setBrand(data));
  }, [scopeStudio]);

  useEffect(() => {
    if (isProductManager) return;
    const params = {};
    if (month !== "all") params.month = month;
    if (serviceType !== "all") params.service_type = serviceType;
    if (scopeStudio) params.studio_id = scopeStudio;
    api.get("/reports/studio-rollup", { params }).then(({ data }) => setRollup(data));
  }, [month, serviceType, scopeStudio]);

  const brandRows = [...(brand?.rows || [])].sort((a, b) =>
    sort === "jobs" ? b.jobs - a.jobs : b.profit - a.profit
  );

  const rollupRows = [...(rollup?.rows || [])].sort((a, b) => {
    if (rollupSort === "name") return a.name.localeCompare(b.name);
    return (b[rollupSort] || 0) - (a[rollupSort] || 0);
  });

  const rollupPaged = usePaged(rollupRows);
  const jobPaged = usePaged(jobs);
  const t = summary?.totals;
  if (isProductManager) return <HqB2BProfit />;
  return (
    <div>
      <PageHead
        title="Studio Profit (B2C)"
        subtitle={user.role === "network_admin" ? "Network-wide" : user.studio_name}
      >
        <ExportButtons
          testidPrefix="profit"
          filename="veloskin-profit-summary"
          title="Profit Summary"
          subtitle={user.role === "network_admin" ? "All studios" : user.studio_name}
          columns={[
            { label: "Month", value: (m) => m.month },
            { label: "Jobs", value: (m) => m.jobs },
            { label: "Revenue", value: (m) => m.revenue },
            { label: "Material cost", value: (m) => m.material_cost },
            { label: "Profit", value: (m) => m.profit },
          ]}
          rows={summary?.monthly || []}
        />
      </PageHead>
      <div className="grid gap-6 sm:grid-cols-3">
        <Kpi label="Revenue" value={money(t?.revenue)} testid="profit-revenue" />
        <Kpi
          label="Deductions (GST + material + installer + commission)"
          value={money(t?.material_cost)}
          testid="profit-material"
        />
        <Kpi label="Profit" value={money(t?.profit)} testid="profit-total" accent="text-emerald-600" />
      </div>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Monthly totals</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="monthly-profit-table">
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Material cost</TableHead>
                <TableHead className="text-right">Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(summary?.monthly || []).map((m) => (
                <TableRow key={m.month} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{m.month}</TableCell>
                  <TableCell className="text-right">{m.jobs}</TableCell>
                  <TableCell className="text-right">{money(m.revenue)}</TableCell>
                  <TableCell className="text-right">{money(m.material_cost)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {money(m.profit)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Quote vs invoice variance by month</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="variance-summary-table">
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Upsell jobs</TableHead>
                <TableHead className="text-right">Discount jobs</TableHead>
                <TableHead className="text-right">Jobs with quotes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(summary?.monthly || []).map((m) => (
                <TableRow key={m.month} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{m.month}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {m.upsell_jobs ?? 0}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-rose-600">
                    {m.discount_jobs ?? 0}
                  </TableCell>
                  <TableCell className="text-right text-slate-500">
                    {(m.upsell_jobs ?? 0) + (m.discount_jobs ?? 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg">Studio rollup</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Select value={scopeStudio || "all"} onValueChange={(v) => setScopeStudio(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px]" data-testid="rollup-studio-filter">
                  <SelectValue placeholder="All studios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All studios</SelectItem>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[150px]" data-testid="rollup-month-filter">
                <SelectValue placeholder="All months" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {(rollup?.months || []).map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger className="w-[200px]" data-testid="rollup-service-filter">
                <SelectValue placeholder="All services" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                {services.map((sv) => (
                  <SelectItem key={sv.id} value={sv.name}>
                    {sv.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="studio-rollup-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                {[
                  ["name", "Studio", "text-left"],
                  ["jobs", "Jobs", "text-right"],
                  ["revenue", "Revenue", "text-right"],
                  ["material_cost", "Cost", "text-right"],
                  ["profit", "Profit", "text-right"],
                  ["avg_ticket", "Avg ticket", "text-right"],
                  ["margin_pct", "Margin %", "text-right"],
                ].map(([key, label, align]) => (
                  <TableHead key={key} className={align}>
                    <button
                      data-testid={`rollup-sort-${key}`}
                      onClick={() => setRollupSort(key)}
                      className={`transition-colors duration-200 hover:text-brand-ink ${
                        rollupSort === key ? "font-semibold text-brand-ink" : ""
                      }`}
                    >
                      {label}
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rollupRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-slate-500">
                    No delivered jobs for this filter.
                  </TableCell>
                </TableRow>
              )}
              {rollupPaged.visible.map((r) => (
                <TableRow
                  key={r.studio_id}
                  data-testid={`rollup-row-${r.studio_id}`}
                  className="hover:bg-brand-tint/60"
                >
                  <TableCell data-label="Studio" className="font-medium">
                    {r.name}
                    <span className="ml-2 text-xs text-slate-400">{r.city}</span>
                  </TableCell>
                  <TableCell data-label="Jobs" className="text-right">{r.jobs}</TableCell>
                  <TableCell data-label="Revenue" className="text-right">{money(r.revenue)}</TableCell>
                  <TableCell data-label="Cost" className="text-right">{money(r.material_cost)}</TableCell>
                  <TableCell data-label="Profit" className="text-right font-semibold text-emerald-600">
                    {money(r.profit)}
                  </TableCell>
                  <TableCell data-label="Avg ticket" className="text-right">{money(r.avg_ticket)}</TableCell>
                  <TableCell data-label="Margin %" className="text-right text-slate-500">{r.margin_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ShowMore paged={rollupPaged} testid="rollup-show-more" />
        </CardContent>
      </Card>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg">
            Brand / model breakdown{" "}
            {isAdmin ? (scopeStudio ? "(selected studio)" : "(network-wide)") : ""}
          </CardTitle>
          <div className="flex items-center gap-2">
            <button
              data-testid="sort-by-profit"
              onClick={() => setSort("profit")}
              className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                sort === "profit" ? "bg-brand text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              Sort by profit
            </button>
            <button
              data-testid="sort-by-jobs"
              onClick={() => setSort("jobs")}
              className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                sort === "jobs" ? "bg-brand text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              Sort by job count
            </button>
            <ExportButtons
              testidPrefix="brand-model"
              filename="veloskin-brand-model-profit"
              title="Brand / Model Profit Breakdown"
              subtitle={user.role === "network_admin" ? "All studios" : user.studio_name}
              columns={[
                { label: "Brand", value: (r) => r.brand },
                { label: "Model", value: (r) => r.model },
                { label: "Jobs", value: (r) => r.jobs },
                { label: "Revenue", value: (r) => r.revenue },
                { label: "Material cost", value: (r) => r.material_cost },
                { label: "Profit", value: (r) => r.profit },
                { label: "Avg profit / job", value: (r) => r.avg_profit },
              ]}
              rows={brandRows}
            />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="brand-model-table">
            <TableHeader>
              <TableRow>
                <TableHead>Brand</TableHead>
                <TableHead>Model</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Material</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Avg / job</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {brandRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-slate-500">
                    No delivered jobs to analyse yet.
                  </TableCell>
                </TableRow>
              )}
              {brandRows.map((r) => (
                <TableRow key={`${r.brand}-${r.model}`} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{r.brand}</TableCell>
                  <TableCell className="text-slate-600">{r.model}</TableCell>
                  <TableCell className="text-right">{r.jobs}</TableCell>
                  <TableCell className="text-right">{money(r.revenue)}</TableCell>
                  <TableCell className="text-right">{money(r.material_cost)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {money(r.profit)}
                  </TableCell>
                  <TableCell className="text-right text-slate-500">{money(r.avg_profit)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {brand?.by_brand?.length > 0 && (
        <Card className="mt-6 border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Rollup by brand</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table data-testid="brand-rollup-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Jobs</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brand.by_brand.map((b) => (
                  <TableRow key={b.brand} className="hover:bg-brand-tint/60">
                    <TableCell className="font-medium">{b.brand}</TableCell>
                    <TableCell className="text-right">{b.jobs}</TableCell>
                    <TableCell className="text-right">{money(b.revenue)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">
                      {money(b.profit)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {leads && (
        <Card className="mt-6 border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Lead source breakdown by studio</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table data-testid="lead-source-studio-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Studio</TableHead>
                  {leads.sources.map((s) => (
                    <TableHead key={s} className="text-right">
                      {s}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.per_studio.map((r) => (
                  <TableRow key={r.studio_id} className="hover:bg-brand-tint/60">
                    <TableCell className="font-medium">{r.name}</TableCell>
                    {leads.sources.map((s) => (
                      <TableCell key={s} className="text-right">
                        {r[s] || 0}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-semibold">{r.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Per-job profit (completed)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="job-profit-table">
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Material</TableHead>
                <TableHead className="text-right">Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobPaged.visible.map((j) => (
                <TableRow key={j.id} className="hover:bg-brand-tint/60">
                  <TableCell className="font-medium">{j.customer_name}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(j.completed_at)}</TableCell>
                  <TableCell className="text-right">{money(j.price)}</TableCell>
                  <TableCell className="text-right">{money(j.material_cost)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {money(j.price - (j.material_cost || 0))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ShowMore paged={jobPaged} testid="job-profit-show-more" />
        </CardContent>
      </Card>
      <CustomAnalysis />
    </div>
  );
}
